import { useEffect, useState, useCallback } from "react";
import http from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { STATUS_RU } from "@/lib/statuses";
import { fmt } from "./AdminKit";
import { printQr } from "@/lib/qr";
import { QrCode, Search } from "lucide-react";

export default function AdminMachines() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get(`/v1/admin/machines`, { params: q ? { q } : {} });
      setRows(r.data || []);
    } catch { toast.error("Не удалось загрузить машины"); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (code) => {
    try {
      const r = await http.get(`/v1/admin/machines/${code}`);
      setDetail(r.data);
    } catch { toast.error("Не удалось открыть машину"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-machines">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold text-white">Машины</h1>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск: код, марка, серийный №, клиент"
            className="pl-8 w-72 bg-slate-700 border-slate-600 text-white" data-testid="machine-search" />
        </div>
      </div>

      <div className="rounded-lg border border-slate-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/50 text-slate-300">
            <tr><th className="text-left p-2">Код</th><th className="text-left p-2">Машина</th><th className="text-left p-2">Серийный №</th><th className="text-left p-2">Клиент</th><th className="text-center p-2">Ремонтов</th><th className="p-2"></th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-4 text-center text-slate-400">Загрузка...</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-slate-400">Машины не найдены</td></tr>}
            {rows.map((m) => (
              <tr key={m.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                <td className="p-2"><button onClick={() => openDetail(m.machine_code)} className="font-mono text-indigo-300 hover:underline" data-testid={`machine-row-${m.machine_code}`}>{m.machine_code}</button></td>
                <td className="p-2 text-slate-200">{[m.brand, m.model].filter(Boolean).join(" ") || "—"}</td>
                <td className="p-2 text-slate-400">{m.serial_number || "—"}</td>
                <td className="p-2 text-slate-400">{m.customer_code || "—"}</td>
                <td className="p-2 text-center text-slate-200">{m.repair_count}</td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="ghost" className="text-indigo-300 hover:text-indigo-200" onClick={() => printQr(m.machine_code, `${m.brand || ""} ${m.model || ""}`.trim())} data-testid={`machine-qr-${m.machine_code}`}><QrCode className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="machine-detail">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2">
            <span className="font-mono text-indigo-300">{detail?.machine?.machine_code}</span>
            <span>{[detail?.machine?.brand, detail?.machine?.model].filter(Boolean).join(" ")}</span>
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">История ремонтов и QR-наклейка машины.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-slate-400">Серийный №:</span> <span className="text-slate-100">{detail.machine.serial_number || "—"}</span></div>
                <div><span className="text-slate-400">Тип:</span> <span className="text-slate-100">{detail.machine.machine_type || "—"}</span></div>
                <div><span className="text-slate-400">Клиент:</span> <span className="text-slate-100">{detail.customer?.name || detail.machine.customer_code || "—"}</span></div>
                <div><span className="text-slate-400">Добавлена:</span> <span className="text-slate-100">{fmt(detail.machine.created_at)}</span></div>
              </div>
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => printQr(detail.machine.machine_code, `${detail.machine.brand || ""} ${detail.machine.model || ""}`.trim())} data-testid="machine-detail-qr"><QrCode className="h-4 w-4 mr-1" />Печать QR-наклейки</Button>
              <div>
                <p className="text-slate-300 font-semibold mb-2">История ремонтов ({detail.history.length})</p>
                <div className="space-y-2">
                  {detail.history.length === 0 && <p className="text-slate-500 text-xs">Пока нет ремонтов.</p>}
                  {detail.history.map((h) => (
                    <div key={h.id} className="rounded border border-slate-700 p-2" data-testid={`machine-hist-${h.order_number}`}>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="font-mono text-blue-300">{h.order_number}</span>
                        <span className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-200">{STATUS_RU[h.status] || h.status}</span>
                        <span className="text-slate-500">{fmt(h.created_at)}</span>
                      </div>
                      <p className="text-slate-300 text-xs mt-1">{h.problem_description || "—"}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
